import React, { useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Menu, Sun, Moon, MessageCircle } from "lucide-react";
import Sidebar from "../components/admin/Sidebar";
import { useTheme } from "../contexts/ThemeContext";
import { useSocketContext } from "../contexts/SocketContext";

const AdminLayout = ({ hideHeader = false }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const { unreadCount, resetUnreadCount } = useSocketContext();
  const location = useLocation();

  // Check if current route is chat to apply fullwidth layout
  const isChatPage = location.pathname.startsWith("/admin/chats");

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-900">
      {/* Sidebar - always visible */}
      <Sidebar isOpen={sidebarOpen} onToggle={toggleSidebar} />

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden lg:ml-0">
        {/* Header - controlled by hideHeader prop */}
        {!hideHeader ? (
          <header className="bg-white border-b border-gray-200 dark:bg-gray-800 dark:border-gray-700 shadow-sm">
            <div className="flex items-center justify-between px-6 py-4">
              <div className="flex items-center gap-4">
                <button
                  onClick={toggleSidebar}
                  className="p-2 rounded-xl text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700 lg:hidden transition-colors"
                >
                  <Menu size={20} />
                </button>
                <h2 className="text-xl font-semibold text-gray-800 dark:text-white">
                  Dashboard
                </h2>
              </div>

              <div className="flex items-center gap-3">
                {/* Notification indicator */}
                {unreadCount > 0 && (
                  <div className="relative">
                    <button className="p-2.5 rounded-xl bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400 transition-all duration-200 hover:scale-105">
                      <MessageCircle size={18} />
                    </button>
                    <span className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-medium shadow-lg">
                      {unreadCount > 99 ? "99+" : unreadCount}
                    </span>
                  </div>
                )}

                {/* Theme toggle */}
                <button
                  onClick={toggleTheme}
                  className="p-2.5 rounded-xl text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700 transition-all duration-200 hover:scale-105"
                >
                  {theme === "light" ? <Moon size={18} /> : <Sun size={18} />}
                </button>

                {/* Profile dropdown */}
                <div className="flex items-center gap-3 pl-3 border-l border-gray-200 dark:border-gray-700">
                  <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                    <span className="text-sm font-semibold text-white">A</span>
                  </div>
                  <div className="hidden md:block">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                      Admin User
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      admin@example.com
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </header>
        ) : null}

        {/* Main content area */}
        <main
          className={`flex-1 overflow-x-hidden overflow-y-auto ${
            isChatPage
              ? "bg-gray-100 dark:bg-gray-900"
              : "bg-gray-50 dark:bg-gray-900"
          }`}
        >
          {isChatPage ? (
            /* Chat page - fullwidth without padding, pass toggleSidebar */
            <Outlet context={{ toggleSidebar }} />
          ) : (
            /* Other pages - with container padding */
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
              <Outlet />
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
